import type { Quote, QuoteLine } from '../api/types'
import { lineFamily } from '../editor/edits'
import type { ShippingState } from '../shipping/shipping-api'
import { chosenConfigurationItems } from '../ui/quote-lines'
import type { OnlinePrice, PricingLineComparison, PricingOutreachRequest } from './pricing-api'

/** Insight tones: green on target, amber close, red under (demo `insightTones`). */
export const insightTones = {
  green: 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300',
  amber: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300',
  red: 'border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300',
  unknown: 'border-border bg-muted/40 text-muted-foreground',
}

// The demo's worksheet rules (`pricing-tab.tsx`, `pricing-insights.tsx`). Net is what we pay the
// manufacturer, Profit is Sell minus Net, Margin is Profit / Sell, markup is Profit / Net.

export type PricingField =
  | 'quantity'
  | 'list_amount'
  | 'discount_chain'
  | 'source_net_amount'
  | 'net_amount'
  | 'sell_amount'
  | 'margin_percent'
  | 'markup_percent'
  | 'profit_amount'

/** Validate one worksheet edit and turn it into the line update body (demo `pricingFieldChange`). */
export function pricingFieldChange(field: PricingField, value: string): Record<string, unknown> {
  const text = value.trim()
  if (field === 'discount_chain') {
    const chain = text ? text.split('/').map((part) => part.trim()) : []
    if (chain.some((part) => !/^\d+(\.\d+)?$/.test(part) || Number(part) > 100)) {
      throw new Error('Use discounts from 0 to 100, separated by /.')
    }
    return { discount_chain: chain }
  }
  if (field === 'quantity' && !/^\d+$/.test(text)) throw new Error('Quantity must be a positive whole number.')
  if (!/^-?\d+(\.\d+)?$/.test(text)) throw new Error('Enter a number.')
  const number = Number(text)
  if (!Number.isFinite(number)) throw new Error('Enter a finite number.')
  if (field === 'quantity' && number <= 0) throw new Error('Quantity must be greater than zero.')
  if (field === 'margin_percent' && number >= 100) throw new Error('Margin must be below 100%.')
  if (field === 'markup_percent' && number < -100) throw new Error('Markup must be at least -100%.')
  if (!['profit_amount', 'margin_percent', 'markup_percent'].includes(field) && number < 0) {
    throw new Error('Enter zero or more.')
  }
  return { [field]: text }
}

type NegotiatedNet = { type: string; standard_net_amount?: string }
export type SpecialPrice = {
  type: string
  code: string
  regular_net_amount: string
  savings_amount: string
  option_net_amount: string
  ends_at: string
}

/** The manufacturer discount a line moved to after it was added (USA-1320). */
export type DiscountChange = {
  type: string
  previous_discount_chain_notation?: string
  previous_amount?: string
  changed_on?: string
}

function adjustmentOfType<T>(line: QuoteLine, types: string[], last = false): T | undefined {
  const items = last ? [...line.pricing_adjustments].reverse() : line.pricing_adjustments
  return items.find(
    (item): item is T =>
      typeof item === 'object' && item !== null && 'type' in item && types.includes(String(item.type)),
  )
}

/** A line's standard and active Net, sell, Profit, markup and pricing source (demo `worksheetPricing`). */
export function worksheetPricing(line: QuoteLine) {
  const adjustment = adjustmentOfType<NegotiatedNet>(line, ['manufacturer_quote', 'net_override'], true)
  const special = adjustmentOfType<SpecialPrice>(line, ['promotion'])
  const chainEntry = adjustmentOfType<DiscountChange>(line, ['manufacturer_discount_chain'])
  const discountChange = chainEntry?.previous_discount_chain_notation ? chainEntry : undefined
  const chainNet = line.discount_chain?.length
    ? line.discount_chain.reduce((net, discount) => net * (1 - Number(discount) / 100), Number(line.list_amount))
    : null
  const standard = Number(
    adjustment?.standard_net_amount ?? line.derived_net_amount ?? line.source_net_amount ?? chainNet ?? line.list_amount,
  )
  const net = Number(line.derived_net_amount ?? line.source_net_amount ?? chainNet ?? line.list_amount)
  const usesNet =
    Boolean(adjustment) ||
    Math.round(net * 100) !==
      Math.round(Number(special?.regular_net_amount ?? chainNet ?? line.source_net_amount ?? line.list_amount) * 100)
  const sell = Number(line.sell_amount ?? line.list_amount)
  const profit = Number(line.totals.profit)
  const markup = Number(line.totals.net) ? (profit / Number(line.totals.net)) * 100 : 0
  return { standard, net, sell, profit, markup, adjustment, special, discountChange, usesNet }
}

/**
 * What a custom item still needs priced (USA-1338). Items imported without a catalog product arrive
 * with no Net or Sell, and entering a Net leaves Sell at zero until Sales sets it.
 */
export function customPriceNeeded(line: QuoteLine) {
  if (line.line_type !== 'custom') return null
  const net = Number(line.derived_net_amount ?? line.source_net_amount ?? line.list_amount) <= 0
  const sell = Number(line.sell_amount ?? line.list_amount) <= 0
  return net && sell ? 'Needs Net and Sell' : net ? 'Needs Net' : sell ? 'Needs Sell' : null
}

/** Installation labor lines, which the worksheet shows as one row priced in Services. */
export function isInstallationLine(line: QuoteLine) {
  return (
    line.line_type === 'service' &&
    line.pricing_adjustments.some(
      (adjustment) =>
        adjustment !== null &&
        typeof adjustment === 'object' &&
        'type' in adjustment &&
        adjustment.type === 'installation_estimate',
    )
  )
}

/** A chosen configuration's sub-row key, beside its product's quote line id. */
export function configurationRowId(lineId: string, group: string) {
  return `configuration:${lineId}:${group}`
}

/** Each line's sub-lines in item order; installation labor is never a sub-line. */
function subLines(lines: QuoteLine[]) {
  const ids = new Set(lines.map((line) => line.id))
  const children = new Map<string, QuoteLine[]>()
  for (const line of lines) {
    if (!line.parent_line_id || !ids.has(line.parent_line_id) || isInstallationLine(line)) continue
    children.set(line.parent_line_id, [...(children.get(line.parent_line_id) ?? []), line])
  }
  return children
}

/** The quote's top lines in item order, leaving out installation labor (its own row). */
export function worksheetRoots(lines: QuoteLine[]) {
  const ids = new Set(lines.map((line) => line.id))
  return lines.filter((line) => !isInstallationLine(line) && !(line.parent_line_id && ids.has(line.parent_line_id)))
}

/**
 * Item numbers as the customer page and PDF number them (USA-1344): top lines 1, 2, 3 in item
 * order, each line's chosen configurations then its sub-lines as 2.1, 2.2 beneath it (demo
 * `equipmentRows`), keyed by `configurationRowId`. Installation labor carries no number there.
 */
export function worksheetLineNumbers(lines: QuoteLine[]) {
  const numbers = new Map<string, string>()
  const childCounts = new Map<string, number>()
  const children = subLines(lines)
  const visit = (line: QuoteLine, number: string) => {
    numbers.set(line.id, number)
    let child = 0
    for (const item of chosenConfigurationItems(line)) {
      numbers.set(configurationRowId(line.id, item.group), `${number}.${++child}`)
    }
    for (const item of children.get(line.id) ?? []) visit(item, `${number}.${++child}`)
    if (child) childCounts.set(line.id, child)
  }
  const roots = worksheetRoots(lines)
  roots.forEach((line, index) => visit(line, String(index + 1)))
  return { numbers, childCounts, rootCount: roots.length }
}

/** How the worksheet lists lines (USA-1344): one list in item order, or grouped. */
export type WorksheetGrouping = 'item' | 'manufacturer' | 'category'

export type WorksheetGroup = {
  key: string
  kind: 'list' | 'manufacturer' | 'category' | 'other'
  label: string
  request?: PricingOutreachRequest
  /** The group's top lines in item order; each shows with its sub-lines. */
  roots: QuoteLine[]
}

/**
 * The worksheet's sections. Grouped, a line's sub-lines stay under it in its group (USA-1140):
 * by its manufacturer's pricing request, or by its product's catalog family ("Cooking
 * Equipment"). Lines with neither are listed last.
 */
export function worksheetGroups(
  lines: QuoteLine[],
  grouping: WorksheetGrouping,
  requests: PricingOutreachRequest[],
): WorksheetGroup[] {
  const roots = worksheetRoots(lines)
  if (grouping === 'item') return [{ key: 'list', kind: 'list', label: 'Quote lines', roots }]
  const groups: WorksheetGroup[] = []
  if (grouping === 'manufacturer') {
    for (const request of requests) {
      const ids = new Set(request.lines.map((item) => item.line_id))
      const members = roots.filter((line) => ids.has(line.id))
      // A manufacturer whose lines all sit under another manufacturer's equipment shows there.
      if (members.length) {
        groups.push({
          key: `manufacturer:${request.manufacturer_id}`,
          kind: 'manufacturer',
          label: request.manufacturer_name,
          request,
          roots: members,
        })
      }
    }
  } else {
    const categories = new Map(
      requests.flatMap((request) => request.lines.map((line) => [line.line_id, line.category] as const)),
    )
    for (const line of roots) {
      const category = categories.get(line.id)
      if (!category) continue
      const key = `category:${category}`
      const group = groups.find((item) => item.key === key)
      if (group) group.roots.push(line)
      else groups.push({ key, kind: 'category', label: category, roots: [line] })
    }
  }
  // Groups sort by name, so they hold still while their lines are reordered.
  groups.sort((left, right) => left.label.localeCompare(right.label))
  const grouped = new Set(groups.flatMap((group) => group.roots.map((line) => line.id)))
  const rest = roots.filter((line) => !grouped.has(line.id))
  const label = groups.length ? 'Other quote lines' : 'Quote lines'
  return [...groups, { key: 'other', kind: 'other', label, roots: rest }]
}

/**
 * The position that puts a line, with its sub-lines, just before `anchor` or just after the
 * anchor's sub-lines: the server lifts the family out, then inserts it at that position.
 */
export function familyPosition(lines: QuoteLine[], line: QuoteLine, anchor: QuoteLine, side: 'before' | 'after') {
  const moving = new Set(lineFamily(lines, line).map((item) => item.id))
  const rest = lines.filter((item) => !moving.has(item.id))
  if (side === 'before') return rest.findIndex((item) => item.id === anchor.id) + 1
  const anchorFamily = new Set(lineFamily(lines, anchor).map((item) => item.id))
  return rest.reduce((last, item, index) => (anchorFamily.has(item.id) ? index : last), -1) + 2
}

/** Plain-text notes, unpacking imported JSON notes to their readable parts (demo `pricingNotes`). */
export function pricingNotes(value: string | null | undefined): string {
  if (!value) return ''
  let text = value
  try {
    const parsed: unknown = JSON.parse(value)
    text =
      typeof parsed === 'string'
        ? parsed
        : parsed && typeof parsed === 'object' && !Array.isArray(parsed)
          ? Object.entries(parsed)
              .filter(([key]) => ['symbol', 'zone', 'dimensions', 'requirements', 'note', 'notes', 'internal_notes'].includes(key))
              .flatMap(([, entry]) => (Array.isArray(entry) ? entry : [entry]))
              .filter((entry): entry is string => typeof entry === 'string')
              .join('; ')
          : ''
  } catch {
    // Ordinary notes are plain text.
  }
  return text.replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '').trim()
}


/** Online prices that compare to ours: the same product and unit, purchasable, in dollars. */
export function comparableOnlinePrices(observations: OnlinePrice[]) {
  return observations.filter(
    (item) =>
      item.status === 'comparable' &&
      item.purchasable &&
      item.currency === 'USD' &&
      Number.isFinite(Number(item.amount)) &&
      Number(item.amount) > 0,
  )
}

/** Average of the comparable online prices, or null when we hold none. Nothing is estimated. */
export function onlineAverage(observations: OnlinePrice[]) {
  const offers = comparableOnlinePrices(observations)
  return offers.length ? offers.reduce((total, item) => total + Number(item.amount), 0) / offers.length : null
}

export type SmartSellBasis = 'map' | 'online' | 'store' | 'margin' | 'floor'

const cents = (value: number) => Math.round(value * 100) / 100

/**
 * The smart sell price (USA-1140), the rule the server uses when a line is added: 1% under the
 * lower of MAP and the lowest comparable online price, capped at our store price; with neither,
 * the store price; with no
 * store price, a 25% Margin on Net. Never below the floor (minimum margin or UMRP) or Net.
 */
export function smartSell({
  net,
  floor,
  map,
  online,
  store,
}: {
  net: number
  floor: number
  map: number | null
  online: number | null
  store: number | null
}): { amount: number; basis: SmartSellBasis } {
  const competitive = [
    { value: map, basis: 'map' as const },
    { value: online, basis: 'online' as const },
  ].filter((item): item is { value: number; basis: 'map' | 'online' } => item.value !== null && item.value > 0)
  let target: { amount: number; basis: SmartSellBasis } | null = null
  if (competitive.length) {
    const lowest = competitive.reduce((best, item) => (item.value < best.value ? item : best))
    target = { amount: cents(lowest.value * 0.99), basis: lowest.basis }
    // Quotes never start above what shopusars.com charges for the same product.
    if (store !== null && store > 0 && cents(store) < target.amount) target = { amount: cents(store), basis: 'store' }
  } else if (store !== null && store > 0) {
    target = { amount: cents(store), basis: 'store' }
  } else if (net > 0) {
    target = { amount: Math.ceil((net / 0.75) * 100 - 1e-6) / 100, basis: 'margin' }
  }
  const minimum = Math.ceil(Math.max(floor, net, 0) * 100 - 1e-6) / 100
  if (!target || target.amount < minimum) return { amount: minimum, basis: 'floor' }
  return target
}

export const smartSellBasisLabels: Record<SmartSellBasis, string> = {
  map: '1% under MAP',
  online: '1% under the lowest online price',
  store: 'Our store price',
  margin: '25% Margin (no MAP, online or store price)',
  floor: 'Minimum price',
}

/** Earlier accepted prices to this customer for the same model and configuration. */
export function matchingCustomerPrices(line: QuoteLine, quote: Quote, history: Quote[]) {
  const configuration = (value: QuoteLine) =>
    JSON.stringify(Object.entries(value.configuration).sort(([a], [b]) => a.localeCompare(b)))
  return history
    .filter((item) => item.id !== quote.id && item.customer_id === quote.customer_id && ['approved', 'started'].includes(item.status))
    .flatMap((item) =>
      item.lines
        .filter(
          (previous) =>
            previous.product_id &&
            previous.product_id === line.product_id &&
            configuration(previous) === configuration(line) &&
            Number(previous.quantity) > 0,
        )
        .map((previous) => ({ quote: item, line: previous })),
    )
}

/** One Margin, markup, sell or Profit across every installation line, as one markup (demo `installationMarkup`). */
export function installationMarkup(lines: QuoteLine[], changes: Record<string, unknown>) {
  const net = lines.reduce((total, line) => total + Number(line.totals.net), 0)
  if (net <= 0) throw new Error('Installation Net must be above zero.')
  if ('markup_percent' in changes) return Number(changes.markup_percent)
  const sell = 'sell_amount' in changes ? Number(changes.sell_amount) : net + Number(changes.profit_amount)
  return (sell / net - 1) * 100
}

/** Delivery buffer against the quote's Latest Deliver By (demo `deliveryRisk`). */
export function deliveryRisk(deadline?: string | null, arrival?: string | null) {
  const end = deadline ? Date.parse(`${deadline}T00:00:00Z`) : NaN
  const delivery = arrival ? Date.parse(`${arrival}T00:00:00Z`) : NaN
  if (!Number.isFinite(end) || !Number.isFinite(delivery)) return { tone: 'unknown' as const, slack: null }
  const slack = Math.round((end - delivery) / 86400000)
  return { tone: slack >= 21 ? ('green' as const) : slack >= 0 ? ('amber' as const) : ('red' as const), slack }
}

/**
 * The line's shipment from the Shipping stage: its ship-ready date and latest arrival estimate,
 * else ship-ready plus the selected method's upper transit range in business days (demo `pricingDelivery`).
 */
export function pricingDelivery(line: QuoteLine, shipping?: ShippingState) {
  const shipment = shipping?.shipments.find((item) => item.lines.some((entry) => entry.line_id === line.id))
  const method = shipping?.methods.find((item) => item.id === (shipping.selected_method ?? 'standard_freight'))
  if (!shipment || !method) return null
  let arrival = shipment.estimated_delivery_date ?? method.estimated_delivery_date ?? null
  const ready = shipment.ship_ready_date ?? null
  const maximum = method.transit_days?.maximum_days
  if (!arrival && ready && Number.isInteger(maximum) && maximum! >= 0) {
    const day = new Date(`${ready}T00:00:00Z`)
    if (!Number.isFinite(day.getTime())) return null
    for (let remaining = maximum!; remaining > 0; ) {
      day.setUTCDate(day.getUTCDate() + 1)
      if (day.getUTCDay() !== 0 && day.getUTCDay() !== 6) remaining--
    }
    arrival = day.toISOString().slice(0, 10)
  }
  return { arrival, ready, method: method.label, planned: !shipping?.selected_method }
}

/** The worksheet's per-line minimum: the manufacturer's UMRP when published, else our minimum margin. */
export function lineMinimum(line: QuoteLine, returned?: PricingLineComparison) {
  if (line.umrp_amount != null) return { amount: Number(line.umrp_amount), kind: 'umrp' as const }
  if (returned?.minimum_sell_unit_amount != null) {
    return { amount: Number(returned.minimum_sell_unit_amount), kind: 'margin' as const }
  }
  return null
}

/**
 * Where Move up / Move down take a line among `peers`, the lines it is listed with (its group's
 * top lines, or its parent's sub-lines): past the neighbor and the neighbor's sub-lines.
 */
export function moveTargets(lines: QuoteLine[], line: QuoteLine, peers: QuoteLine[]) {
  const index = peers.findIndex((item) => item.id === line.id)
  const previous = index > 0 ? peers[index - 1] : undefined
  const next = index >= 0 ? peers[index + 1] : undefined
  return {
    up: previous ? familyPosition(lines, line, previous, 'before') : undefined,
    down: next ? familyPosition(lines, line, next, 'after') : undefined,
  }
}

/** The company-wide minimum Margin on quotes: a suggestion Sales can price below. */
export const MINIMUM_MARGIN_PERCENT = 5

/**
 * Whether a sell price needs a second look (USA-1140): above what the customer can pay elsewhere
 * (our store price or the lowest online price; MAP only when we know neither), or under the
 * minimum Margin. Both are warnings; the price still saves. When that price is itself under our
 * minimum, no sell can pass both checks, so a sell held at the minimum is explained, not red
 * (USA-1261).
 */
export function sellCheck({
  sell,
  net,
  minimum,
  map,
  online,
  store,
}: {
  sell: number
  net: number
  minimum: number | null
  map: number | null
  online: number | null
  store: number | null
}): { tone: 'green' | 'red' | 'unknown'; reason: string | null } {
  type Ceiling = { value: number; label: string; under: string }
  const known = [
    { value: store, label: 'Above our store price', under: 'Our store price is below our minimum margin' },
    { value: online, label: 'Above the lowest online price', under: 'Online is below our minimum margin' },
  ].filter((item): item is Ceiling => item.value !== null && item.value > 0)
  const ceilings: Ceiling[] = known.length
    ? known
    : map !== null && map > 0
      ? [{ value: map, label: 'Above MAP', under: 'MAP is below our minimum margin' }]
      : []
  const ceiling = ceilings.reduce<Ceiling | null>((best, item) => (best === null || item.value < best.value ? item : best), null)
  const floor = minimum ?? (net > 0 ? net / (1 - MINIMUM_MARGIN_PERCENT / 100) : null)
  if (ceiling && floor !== null && ceiling.value < floor - 0.004 && Math.abs(sell - floor) <= 0.005) {
    return { tone: 'unknown', reason: ceiling.under }
  }
  if (ceiling && sell > ceiling.value + 0.004) return { tone: 'red', reason: ceiling.label }
  if (floor !== null && sell < floor - 0.004) return { tone: 'red', reason: 'Below the minimum margin' }
  return { tone: 'green', reason: null }
}
